import React, { useMemo, useRef } from 'react';
import { arrayOf, string } from 'prop-types';
import classNames from 'classnames';
import { Carousel, ListingCardCompact, NamedLink } from '..';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { useSelector } from 'react-redux';
import { createURLParamsForListing } from '../../util/listingHelpers';
import { LOCALE_TO_LANG } from '../../constants/locales';
import ListingService from '../../services/ListingService';

import css from './SectionListings.module.css';
import { useConfiguration } from '../../context/configurationContext';

// Helper to get localized listing title for SEO (using preloaded translations)
const getLocalizedListingTitle = (listing, language, intl) => {
  const publicData = listing?.attributes?.publicData;
  const discipline = publicData?.discipline;
  const cityName = publicData?.multiLangAddressParts?.[language]?.city;
  const authorName = listing?.author?.attributes?.profile?.displayName;
  
  // Get translated discipline from preloaded messages (no intl.formatMessage needed)
  const translatedDiscipline = discipline 
    ? ListingService.generalMessages[language]?.[`General.${discipline}`] || discipline
    : null;
  
  // Get translated "in" word
  const inText = ListingService.generalMessages[language]?.['General.in'] || 'in';
  
  // Get translated "with" word
  const withText = ListingService.generalMessages[language]?.['General.with'] || 'with';
  
  // Build title: "translated discipline in city with author"
  let title = '';
  
  if (translatedDiscipline) {
    title += translatedDiscipline.toLowerCase();
  }
  
  if (cityName) {
    title += ` ${inText} ${cityName}`;
  }
  
  if (authorName) {
    title += ` ${withText} ${authorName}`;
  }
  
  return title.trim() || listing?.attributes?.title || '';
};;

const SectionListings = props => {
  const { rootClassName, className, listings } = props;

  const { currentUser, interfaceLang } = useSelector(state => ({ 
    currentUser: state.user.currentUser,
    interfaceLang: state.ui.interfaceLang
  }));
  const config = useConfiguration();
  const intl = useIntl();
  const sliderRef = useRef();
  
  // Get language for localization (works in SSR)
  const language = LOCALE_TO_LANG[interfaceLang] || 'english';

  const classes = useMemo(() => classNames(rootClassName || css.root, className), [
    className,
    rootClassName,
  ]);

  const handlePrev = () => {
    if (sliderRef.current) sliderRef.current.slickPrev();
  };
  
  const handleNext = () => {
    if (sliderRef.current) sliderRef.current.slickNext();
  };

  const hasListings = listings && listings.length > 0;
  
  // Skip visual carousel in SSR (only show hidden nav for crawlers)
  const isSSR = typeof window === 'undefined';

  const popularListings = useMemo(
    () =>
      hasListings && !isSSR && (
        <Carousel
          ref={sliderRef}
          columns={5}
          className={css.carousel}
          contentClassName={css.carouselContent}
          showArrows={false}
        >
          {listings.map(l => (
            <ListingCardCompact
              key={l.id.uuid}
              className={css.listingCard}
              listing={l}
              currentUser={currentUser}
              config={config}
            />
          ))}
        </Carousel>
      ),
    [config, currentUser, listings, hasListings, isSSR]
  );

  return (
    <div className={classes}>
      {/* Hidden navigation links for SEO crawlers - using sr-only pattern */}
      {hasListings && (
        <nav className="sr-only" style={{ 
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0
        }}>
          <ul>
            {listings.map(listing => {
              // Generate localized title and URL params using Redux state (SSR-compatible)
              const localizedTitle = getLocalizedListingTitle(listing, language, intl);
              const urlParams = createURLParamsForListing(listing, interfaceLang, intl);
              return (
                <li key={listing.id.uuid}>
                  <NamedLink name="ListingPage" params={urlParams}>
                    {localizedTitle}
                  </NamedLink>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
      
      <div className={css.listingsContent}>
        {hasListings && (
          <div>
            <div className={css.headerRow}>
              <h2 className={css.title}>
                <FormattedMessage id="SectionListings.popularListingsTitle" />
              </h2>
              {!isSSR && (
                <div className={css.carouselArrows}>
                  <button className={css.arrowBtn} onClick={handlePrev} aria-label="Previous">
                    ‹
                  </button>
                  <button className={css.arrowBtn} onClick={handleNext} aria-label="Next">
                    ›
                  </button>
                </div>
              )}
            </div>
            <div className={css.carouselWrapper} style={isSSR ? { minHeight: 0 } : undefined}>
              {popularListings}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SectionListings.defaultProps = {
  rootClassName: null,
  className: null,
  listings: [],
};

SectionListings.propTypes = {
  rootClassName: string,
  className: string,
  listings: arrayOf(propTypes.listing),
};

export default SectionListings;
